import { useEffect } from "react";
import {
  ArrowRight,
  Calendar,
  FileText,
  Headphones,
  Phone,
  User,
  X,
} from "lucide-react";
import Button from "../ui/Button";
import StatusBadge from "./StatusBadge";
import { formatDateTime } from "../../data/mock/dashboardData";
import { formatPersonName } from "../../utils/personName";

const OUTCOME_STATUS = {
  "Payment plan agreed": "active",
  "Promise to pay recorded": "pending",
  "Partial payment taken": "completed",
  "Customer educated": "open",
  "No answer": "failed",
};

export function OutcomeSummaryBadge({ outcome }) {
  const status = OUTCOME_STATUS[outcome] ?? "open";

  return (
    <StatusBadge
      status={status}
      label={outcome}
    />
  );
}

export default function ContactOutcomeModal({ log, onClose }) {
  useEffect(() => {
    if (!log) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [log, onClose]);

  if (!log) return null;

  const details = log.outcomeDetails ?? { summary: "", fields: [], followUpDate: null, followUpNotes: "" };
  const summary = details.summary || log.summary || "";
  const followUpWhen = details.followUpDate || log.followUpDate || log.follow_up_date || "";
  const followUpNotes = details.followUpNotes || log.followUpNotes || log.follow_up_notes || "";
  const capturedFields = (details.fields || []).filter((field) => field?.value);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="outcome-modal-title"
        aria-modal="true"
        className="flex max-h-[min(90vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-primary via-primary-light to-accent" aria-hidden />
        <div className="border-b border-primary/[0.06] bg-gradient-to-r from-primary/[0.03] to-accent/[0.05] px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <OutcomeSummaryBadge outcome={log.outcome} />
                <span className="rounded-md bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-primary/45 ring-1 ring-primary/[0.06]">
                  {log.id}
                </span>
              </div>
              <h2 id="outcome-modal-title" className="text-lg font-bold text-primary">
                Contact outcome details
              </h2>
              <p className="mt-1 text-sm text-primary/55">
                Full record logged by SimCollect after the communication session.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-primary/45 transition-colors hover:bg-white hover:text-primary"
              aria-label="Close outcome details"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <MetaItem icon={User} label="Customer" value={formatPersonName(log)} />
            <MetaItem icon={FileText} label="Case" value={log.caseId} />
            <MetaItem icon={Headphones} label="Logged by" value={log.agent} />
            <MetaItem icon={Phone} label="Channel" value={log.type} />
            <MetaItem icon={Calendar} label="Logged" value={formatDateTime(log.loggedAt)} />
          </div>

          {summary && (
            <section className="mt-5 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">Outcome summary</p>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-primary/80">{summary}</p>
            </section>
          )}

          {capturedFields.length > 0 && (
            <section className="mt-5">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                Captured on this engagement
              </p>
              <dl className="grid gap-2 sm:grid-cols-2">
                {capturedFields.map((field) => (
                  <div
                    key={field.label}
                    className="rounded-xl border border-primary/[0.06] bg-white px-3.5 py-3 shadow-[0_1px_2px_rgba(127,29,29,0.04)]"
                  >
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/40">
                      {field.label}
                    </dt>
                    <dd className="mt-1 text-sm font-semibold leading-snug text-primary">{field.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {(followUpWhen || followUpNotes) && (
            <section className="mt-5 flex items-start gap-3 rounded-xl border border-accent/25 bg-accent/[0.08] px-4 py-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
                <ArrowRight className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/50">Follow-up</p>
                {followUpWhen ? (
                  <p className="mt-1 text-sm font-semibold text-primary">{followUpWhen}</p>
                ) : null}
                {followUpNotes ? (
                  <p className="mt-1 text-sm leading-relaxed text-primary/80">{followUpNotes}</p>
                ) : null}
              </div>
            </section>
          )}
        </div>

        <div className="flex justify-end border-t border-primary/[0.06] bg-primary/[0.02] px-5 py-4 sm:px-6">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function MetaItem({ icon: Icon, label, value }) {
  if (value == null || value === "" || value === "—") return null;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-primary/[0.06] bg-white px-3.5 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/[0.06] text-primary/55">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/40">{label}</p>
        <p className="mt-0.5 truncate text-sm font-semibold text-primary">{value}</p>
      </div>
    </div>
  );
}
